import { useEffect, useRef, useState } from 'react';

type Effect = () => void;

type AsyncEffect = () => Promise<void>;

/**
 *	As the name implies, it retuns a function that will force a component update when it is called.
 *
 *	@returns Function
 */
export function useForceUpdate() {
	const [, s] = useState<number>(0);

	return () => s((v) => ++v);
}

/**
 *	Behaves like useEffect, but only once, useful to circumvent React's development mount-remount.
 *
 *	@param effect Effect
 */
export function useEffectOnce(effect: Effect): void {
	const destroyEffectRef = useRef<any>(null);
	const didCallEffectRef = useRef<boolean>(false);
	const renderAfterEffectRef = useRef<boolean>(false);

	const forceUpdate = useForceUpdate();

	if (didCallEffectRef.current) {
		renderAfterEffectRef.current = true;
	}

	useEffect(() => {
		if (!didCallEffectRef.current) {
			destroyEffectRef.current = effect();
			didCallEffectRef.current = true;
		}

		forceUpdate();

		return () => {
			// Ignore dummy useEffect issue with React 18.
			if (!renderAfterEffectRef.current) {
				return;
			}

			if (destroyEffectRef.current) {
				destroyEffectRef.current();
			}
		};
	}, []);
}

/**
 *	Fires once component mounts the first time, additionally it can take a second effect on unmount.
 *
 *	@param onMount Effect | AsyncEffect
 *	@param onUnmount Effect
 */
export function useComponentDidMount(
	onMount: Effect | AsyncEffect,
	onUnmount?: Effect
): void {
	useEffectOnce(() => {
		onMount();

		if (onUnmount) {
			return onUnmount;
		}
	});
}

/**
 *	Fires on component unmount, it's effect is conditional in cases where an unmount action is conditional.
 *
 *	@param onUnmount Effect
 */
export function useComponentWillUnmount(onUnmount?: Effect): void {
	useEffectOnce(() => {
		return onUnmount ? onUnmount : null;
	});
}
